import { isUserAdmin } from "common/roles/role";
import { GetOAuthToken, GetStateStorage } from "common/storage";

/**
 * Frontend-only access restriction for specific admin accounts.
 *
 * Key               :- id of an admin account that must be restricted.
 * paths             :- admin route path prefixes this user IS allowed to open.
 *                      Every other admin-only screen is blocked.
 * blockedFeatures   :- in-page features hidden even inside an allowed screen.
 * sessionSwitchBlockedPaths :- agent screens hidden while this admin is session-switched into an agent.
 *
 * Start with an empty `paths` (everything disabled) and add paths as we enable
 * screens. Admins that are not listed here keep the current behaviour.
 *
 * Note: this only hides menu items/buttons and blocks routes in the UI; APIs are not affected.
 */
export const ADMIN_FEATURES = {
  MAILPRO_UPDATE_GLOBAL_NOTE: "MAILPRO_UPDATE_GLOBAL_NOTE",
  MAILPRO_GENERATE_PMA_PDFS: "MAILPRO_GENERATE_PMA_PDFS",
  MAILPRO_PAUSE_AREA_AGENT: "MAILPRO_PAUSE_AREA_AGENT",
  MAILPRO_SPECIAL_PRICING: "MAILPRO_SPECIAL_PRICING",
  MAILPRO_MARKET_SUMMARY: "MAILPRO_MARKET_SUMMARY",
  SESSION_SWITCH: "SESSION_SWITCH",
  TOPBAR_FAILED_INVOICES: "TOPBAR_FAILED_INVOICES",
};

export const RESTRICTED_ADMINS = {
  "812345": {
    paths: ["/agent/mailpro/insights"],
    blockedFeatures: [
      ADMIN_FEATURES.MAILPRO_UPDATE_GLOBAL_NOTE,
      ADMIN_FEATURES.MAILPRO_GENERATE_PMA_PDFS,
      ADMIN_FEATURES.MAILPRO_PAUSE_AREA_AGENT,
      ADMIN_FEATURES.MAILPRO_SPECIAL_PRICING,
      ADMIN_FEATURES.MAILPRO_MARKET_SUMMARY,
      ADMIN_FEATURES.SESSION_SWITCH,
      ADMIN_FEATURES.TOPBAR_FAILED_INVOICES,
    ],
    sessionSwitchBlockedPaths: [
      "/account", // Update Email or Password
      "/cards/credit", // Manage Credit Cards
      "/agent/year-end-statement", // Year End Statement
      "/agent/areas-by-city", // Neighborhood Selection Tool
    ],
  },
};

export const getOAuthData = () => {
  try {
    const token = GetOAuthToken();
    return token ? JSON.parse(token) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Possible ids of the logged-in admin's own account. During a session switch
 * the admin's data lives in oldSessionData, so only that is used. Otherwise we
 * also look at the app state, since the token's userID may not be the listgenUserID.
 */
export const getRealAdminUserIDs = (oauthData) => {
  if (!oauthData) return [];
  const fromData = (d) => (d ? [d.userID, (d.userData || {}).listgenUserID] : []);
  let candidates;
  if (oauthData.oldSessionData) {
    candidates = fromData(oauthData.oldSessionData);
  } else {
    let stateData = null;
    try {
      stateData = GetStateStorage();
    } catch (e) {
      stateData = null;
    }
    candidates = [...fromData(oauthData), ...fromData(stateData)];
  }
  return candidates.filter(Boolean).map(String);
};

const getRestriction = (oauthData) => {
  if (!oauthData || !isUserAdmin(oauthData.roleID)) return null;
  return findRestriction(oauthData);
};

const findRestriction = (oauthData) => {
  const id = getRealAdminUserIDs(oauthData).find((candidate) =>
    Object.prototype.hasOwnProperty.call(RESTRICTED_ADMINS, candidate)
  );
  return id ? RESTRICTED_ADMINS[id] : null;
};

const matchesPrefix = (path, prefix) =>
  path === prefix || path.startsWith(prefix.endsWith("/") ? prefix : prefix + "/");

// During a session switch the token's roleID is the agent's; the admin lives in oldSessionData.
export const isSessionSwitchPathBlocked = (path, oauthData = getOAuthData()) => {
  if (!oauthData || !oauthData.oldSessionData) return false;
  // oldSessionData keeps the admin's role as userRole
  const oldRole = oauthData.oldSessionData.userRole || oauthData.oldSessionData.roleID;
  if (!isUserAdmin(oldRole)) return false;
  const restriction = findRestriction(oauthData);
  return (
    !!restriction &&
    (restriction.sessionSwitchBlockedPaths || []).some((prefix) =>
      matchesPrefix(path || "", prefix)
    )
  );
};

export const canTokenAccessAdminPath = (oauthData, path) => {
  const restriction = getRestriction(oauthData);
  if (!restriction) return true;
  return restriction.paths.some((prefix) => matchesPrefix(path || "", prefix));
};

export const isAdminFeatureBlocked = (feature, oauthData = getOAuthData()) => {
  const restriction = getRestriction(oauthData);
  return !!restriction && (restriction.blockedFeatures || []).includes(feature);
};
